import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { humanize } from './utils';

export type Lang = 'en' | 'ar';

const LANG_KEY = 'stable.lang';
let current: Lang = (() => {
  try {
    return localStorage.getItem(LANG_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
})();

export function getLang(): Lang {
  return current;
}

/** UI chrome strings. Field labels fall back to humanized keys (with Arabic for the common ones). */
const AR: Record<string, string> = {
  // Navigation
  Dashboard: 'لوحة التحكم', Today: 'اليوم', Schedule: 'الجدول', Bookings: 'الحجوزات', 'Check-in': 'تسجيل الحضور', Waitlist: 'قائمة الانتظار',
  Services: 'الخدمات', Customers: 'العملاء', People: 'الأشخاص', Horses: 'الخيول', Health: 'الصحة', 'Health due': 'المستحقات الصحية',
  'History documents': 'وثائق التاريخ', Training: 'التدريب', Rides: 'الجولات', Boarding: 'الإيواء', Sales: 'المبيعات', Memberships: 'العضويات',
  'Membership plans': 'خطط العضوية', Packages: 'الباقات', Offers: 'العروض', 'Gift cards': 'بطاقات الهدايا', Referrals: 'الإحالات', Finance: 'المالية',
  Orders: 'الطلبات', Payments: 'المدفوعات', Invoices: 'الفواتير', Engagement: 'التفاعل', Inbox: 'صندوق الرسائل', Reviews: 'التقييمات',
  Announcements: 'الإعلانات', Occasions: 'المناسبات', Website: 'الموقع', Pages: 'الصفحات', Blog: 'المدونة', FAQs: 'الأسئلة الشائعة',
  Banners: 'اللافتات', Gallery: 'المعرض', Operations: 'العمليات', Tasks: 'المهام', Rota: 'المناوبات', Stalls: 'الإسطبلات', Inventory: 'المخزون',
  Events: 'الفعاليات', Waivers: 'الإقرارات', Analytics: 'التحليلات', Settings: 'الإعدادات', 'Team & roles': 'الفريق والأدوار', 'Audit log': 'سجل التدقيق',
  'Recurring schedules': 'الجداول المتكررة', 'Horses & care': 'الخيول والرعاية', 'Tracked rides': 'الجولات المسجلة', Yard: 'الإسطبل', 'Task templates': 'قوالب المهام',
  Growth: 'النمو', Locations: 'المواقع', Notifications: 'الإشعارات', Stables: 'الإسطبلات', 'Feature flags': 'مفاتيح الميزات',
  Platform: 'المنصة', 'Operations & stable': 'العمليات والإسطبل', Care: 'الرعاية', Customers_section: 'العملاء',
  // Actions
  Create: 'إنشاء', New: 'جديد', Save: 'حفظ', Cancel: 'إلغاء', Delete: 'حذف', Edit: 'تعديل', Close: 'إغلاق', Search: 'بحث', Filters: 'التصفية',
  'Load more': 'تحميل المزيد', Refresh: 'تحديث', 'Sign out': 'تسجيل الخروج', 'Sign in': 'تسجيل الدخول', Confirm: 'تأكيد', Back: 'رجوع',
  Actions: 'الإجراءات', Details: 'التفاصيل', 'No results': 'لا توجد نتائج', Loading: 'جارٍ التحميل', 'Switch stable': 'تبديل الإسطبل',
  Theme: 'المظهر', Language: 'اللغة', 'Command palette': 'لوحة الأوامر', Upload: 'رفع', Download: 'تنزيل', Open: 'فتح', Apply: 'تطبيق', Clear: 'مسح',
  'Are you sure?': 'هل أنت متأكد؟', 'Saved': 'تم الحفظ', 'Something went wrong': 'حدث خطأ ما', Yes: 'نعم', No: 'لا', All: 'الكل', View: 'عرض',
  // Fields
  name: 'الاسم', status: 'الحالة', email: 'البريد الإلكتروني', phone: 'الهاتف', price: 'السعر', total: 'الإجمالي', amount: 'المبلغ',
  'first name': 'الاسم الأول', 'last name': 'اسم العائلة', type: 'النوع', kind: 'النوع', title: 'العنوان', description: 'الوصف', active: 'نشط',
  date: 'التاريخ', notes: 'ملاحظات', capacity: 'السعة', horse: 'الحصان', person: 'الشخص', service: 'الخدمة', coach: 'المدرب', created: 'أُنشئ',
};

export function translate(text: string, lang: Lang = current): string {
  if (lang === 'en') return text;
  return AR[text] ?? AR[text.toLowerCase()] ?? text;
}

/** Field label for an API property key, e.g. `priceAmount` → "Price amount". */
export function fieldLabel(key: string, lang: Lang = current): string {
  const h = humanize(key);
  return lang === 'ar' ? AR[h.toLowerCase()] ?? h : h;
}

interface I18nValue {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  setLang(lang: Lang): void;
  t(text: string): string;
  label(key: string): string;
}

const Ctx = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(current);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    current = l;
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      // ignore
    }
    setLangState(l);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({ lang, dir: lang === 'ar' ? 'rtl' : 'ltr', setLang, t: (s) => translate(s, lang), label: (k) => fieldLabel(k, lang) }),
    [lang, setLang],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n outside provider');
  return v;
}
